import * as React from 'react';
import {
    AdminUserSearchResult,
    AdminUserSearchResultExtended,
    DomainWithFilespaces,
    FilespaceExtended, Invoice
} from 'fs-api-client';
import { UserFilespaces } from '../user/UserFilespaces';
import { UserDetails } from '../user/UserDetails';
import { Billing } from '../billing/Billing';
import { UserStatusChangeConfirmModal } from '../user/UserStatusChangeConfirmModal';
import { UserInvoices } from '../user/UserInvoices';
import { formatDataSize } from '../../../webportal/utils/utilFunctions';
import { renderStripeIcon } from '../../../webportal/components/common/Icons';
import { BackOfficeEvent } from './BackOfficeEvents';
import { UserDeleteConfirmModal } from '../user/UserDeleteConfirmModal';
import { BillingService } from '../../BillingService';
import { SecurityScope } from '../../utils/SecurityScope';

interface BackOfficeUserProps {
    domain?: DomainWithFilespaces;
    user: AdminUserSearchResult;
    userExtra?: AdminUserSearchResultExtended;
    securityScopes: string [];
    onSuccess: () => Promise<void>;
}

interface BackOfficeUserState {
    doRenderUserFilespaces: boolean;
    doRenderUserDetails: boolean;
    doRenderUserBillingDetails: boolean;
    doRenderUserInvoices: boolean;
    isModalShown: boolean;
    isBarDisabled: boolean;
    isAddPlanButtonDisabled: boolean;
    isDeleteUserModalShown: boolean;
    invoices: Invoice[];
}

class User extends React.Component<BackOfficeUserProps, BackOfficeUserState> {
    private callback: () => void;
    constructor(props: BackOfficeUserProps) {
        super(props);

        this.state = {
            doRenderUserFilespaces: false,
            doRenderUserDetails: true,
            doRenderUserBillingDetails: false,
            doRenderUserInvoices: false,
            isModalShown: false,
            isBarDisabled: false,
            isAddPlanButtonDisabled: true,
            isDeleteUserModalShown: false,
            invoices: [],
        };

        this.renderUserFilespacesDetailsSearchResults = this.renderUserFilespacesDetailsSearchResults.bind(this);
        this.onFilespaceButtonClicked = this.onFilespaceButtonClicked.bind(this);
        this.onUserDetailsButtonClicked = this.onUserDetailsButtonClicked.bind(this);
        this.onBillingDetailsButtonClicked = this.onBillingDetailsButtonClicked.bind(this);
        this.renderUserSearchResultsDetails = this.renderUserSearchResultsDetails.bind(this);
        this.showModal = this.showModal.bind(this);
        this.addNewPlanPackageParentCallback = this.addNewPlanPackageParentCallback.bind(this);
        this.addPlan = this.addPlan.bind(this);
        this.closeModal = this.closeModal.bind(this);
        this.disableAddPlanButton = this.disableAddPlanButton.bind(this);
        this.calculateTotalFilespaceSize = this.calculateTotalFilespaceSize.bind(this);
        this.renderUserStatusButton = this.renderUserStatusButton.bind(this);
        this.handleDisableTopBar = this.handleDisableTopBar.bind(this);
        this.renderConfirmModal = this.renderConfirmModal.bind(this);
        this.showInvoices = this.showInvoices.bind(this);
        this.checkForRequiredSecurityScope = this.checkForRequiredSecurityScope.bind(this);
        this.getNumberOfActiveFilespaces = this.getNumberOfActiveFilespaces.bind(this);
        this.showConfirmDeleteModal = this.showConfirmDeleteModal.bind(this);
        this.hideConfirmDeleteModal = this.hideConfirmDeleteModal.bind(this);
        this.renderConfirmDeleteModal = this.renderConfirmDeleteModal.bind(this);
        this.renderDeleteUserButton = this.renderDeleteUserButton.bind(this);
    }

    async componentDidMount() {
        document.addEventListener(BackOfficeEvent.CloseModal, this.closeModal);
        await this.fetchInvoices();
    }

    render() {
        const topBarClassName = this.state.isBarDisabled ? 'disable' : '';
        const addPlanButtonClassName = this.state.isAddPlanButtonDisabled ? 'disable' : '';
        return (
            <React.Fragment>
                <header className={topBarClassName}>
                    <ul className="row">
                        <li>
                            {!this.state.doRenderUserDetails &&
                            <a onClick={this.onUserDetailsButtonClicked} href="javascript:void(0)">Account Details</a>}
                            {this.state.doRenderUserDetails && <strong>Account Details</strong>}
                        </li>
                        <li>
                            {!this.state.doRenderUserFilespaces &&
                            <a onClick={this.onFilespaceButtonClicked} href="javascript:void(0)">Filespaces</a>}
                            {this.state.doRenderUserFilespaces && <strong>Filespaces</strong>}
                        </li>
                        <li>
                            {!this.state.doRenderUserBillingDetails &&
                            <a onClick={this.onBillingDetailsButtonClicked} href="javascript:void(0)">Billing</a>}
                            {this.state.doRenderUserBillingDetails && <strong>Billing</strong>}
                        </li>
                        <li>
                            {!this.state.doRenderUserInvoices &&
                            <a onClick={this.showInvoices} href="javascript:void(0)">Invoices</a>}
                            {this.state.doRenderUserInvoices && <strong>Invoices</strong>}
                        </li>
                    </ul>
                    <div className="row buttons right">
                        {this.renderStripeLink()}
                        {this.renderUserStatusButton()}
                        {this.renderDeleteUserButton()}
                        {this.state.doRenderUserBillingDetails &&
                        !this.state.isAddPlanButtonDisabled && <button
                            className={addPlanButtonClassName + 'hide'}
                            disabled={this.state.isAddPlanButtonDisabled}
                            onClick={this.addPlan}
                        >
                            Add plan
                        </button>}
                    </div>
                </header>
                <section className="centerBox">
                    {this.state.doRenderUserFilespaces && this.renderUserFilespacesDetailsSearchResults()}
                    {this.state.doRenderUserDetails && this.renderUserSearchResultsDetails()}
                    {this.state.doRenderUserBillingDetails && this.renderUserBillingDetails()}
                    {this.state.doRenderUserInvoices && <UserInvoices userId={this.props.user.id}/>}
                </section>
                {this.state.isModalShown && this.renderConfirmModal(this.props.user.id, this.props.user.email, this.props.user.isSuspended)}
                {this.state.isDeleteUserModalShown && this.renderConfirmDeleteModal()}
            </React.Fragment>
        );
    }

    private disableAddPlanButton(): void {
        this.setState({isAddPlanButtonDisabled: true});
    }

    private showInvoices(): void {
        this.setState(
            {
                doRenderUserDetails: false,
                doRenderUserFilespaces: false,
                doRenderUserBillingDetails: false,
                doRenderUserInvoices: true
            });
    }

    private addPlan(): void {
        if (this.callback) {
            this.callback();
        }
    }

    private checkForRequiredSecurityScope(): boolean {
        return this.props.securityScopes.includes('adminWrite') || this.props.securityScopes.includes('master');
    }

    private renderUserStatusButton(): JSX.Element {
        return (
            <React.Fragment>
                {!this.props.user.isSuspended && this.checkForRequiredSecurityScope() &&
                <button
                    className="warning"
                    data-toggle="modal"
                    data-target="#confirmModal"
                    onClick={this.showModal}
                >
                    Suspend
                </button>}

                {this.props.user.isSuspended && this.checkForRequiredSecurityScope() &&
                <button
                    className="warning"
                    data-toggle="modal"
                    data-target="#confirmModal"
                    onClick={this.showModal}
                >
                    Unsuspend
                </button>}
            </React.Fragment>
        );
    }

    private renderStripeLink(): JSX.Element | null {
        if (this.props.userExtra && this.props.userExtra.stripeLink) {
            return (
                <a target="_blank" href={this.props.userExtra.stripeLink}>
                    {renderStripeIcon()}
                </a>
        );
        }
        return null;
    }

    private showModal(): void {
        this.setState({isModalShown: true});
    }

    private closeModal(): void {
        this.setState({isModalShown: false});
    }

    private handleDisableTopBar(): void {
        if (this.state.isBarDisabled) {
            this.setState({isBarDisabled: false});
        }
        if (!this.state.isBarDisabled) {
            this.setState({isBarDisabled: true});
        }
    }

    private renderConfirmModal(userId: string, userEmail: string, userSuspended: boolean): JSX.Element | null {
        if ( this.props.domain ) {
            return (
                <UserStatusChangeConfirmModal
                    numberOfFilespaces={this.getNumberOfActiveFilespaces()}
                    totalFilespaceSize={this.calculateTotalFilespaceSize(this.props.domain.filespaces)}
                    isUserSuspended={userSuspended}
                    username={userEmail}
                    userId={userId}
                    isModalShown={this.state.isModalShown}
                    onSuccess={this.props.onSuccess}
                />
            );
        }
        if (!this.props.domain) {
            return (
                <UserStatusChangeConfirmModal
                    isUserSuspended={userSuspended}
                    username={userEmail}
                    userId={userId}
                    isModalShown={this.state.isModalShown}
                    onSuccess={this.props.onSuccess}
                />
            );
        }
        return null;
    }

    private getNumberOfActiveFilespaces(): number {
        if (!this.props.domain) {
            return 0;
        }

        return this.props.domain.filespaces.filter(filespace => !filespace.isDeleted).length;
    }

    private calculateTotalFilespaceSize(filespaces: FilespaceExtended[]): string {
        let totalFilespaceSize: number = 0;
        filespaces.forEach((filespace) => {
            if (filespace.stats && !filespace.isDeleted) {
                totalFilespaceSize += filespace.stats.filespaceSize;
            }
        });
        return formatDataSize(totalFilespaceSize);
    }

    private onUserDetailsButtonClicked(): void {
        this.setState(
            {
                doRenderUserFilespaces: false,
                doRenderUserDetails: true,
                doRenderUserBillingDetails: false,
                doRenderUserInvoices: false
            });
    }

   private onFilespaceButtonClicked(): void {
       this.setState(
           {
               doRenderUserFilespaces: true,
               doRenderUserDetails: false,
               doRenderUserBillingDetails: false,
               doRenderUserInvoices: false
           });
    }

    private onBillingDetailsButtonClicked(): void {
        this.setState(
            {
                doRenderUserBillingDetails: true,
                doRenderUserFilespaces: false,
                doRenderUserDetails: false,
                doRenderUserInvoices: false
        });
    }

    private renderUserFilespacesDetailsSearchResults(): JSX.Element {
        return <UserFilespaces domain={this.props.domain} securityScopes={this.props.securityScopes} />;
    }

    private renderUserSearchResultsDetails(): JSX.Element {
        return (
            <UserDetails
                user={this.props.user}
                userExtra={this.props.userExtra}
                domain={this.props.domain}
                securityScopes={this.props.securityScopes}
                fetchUsers={this.props.onSuccess}
            />
        );
    }

    private renderUserBillingDetails(): JSX.Element {
        return (
            <Billing
                key={this.props.user.id}
                disableAddPlanButton={this.disableAddPlanButton}
                handleDisableTopBarStatus={this.handleDisableTopBar}
                user={this.props.user}
                setParentCallback={this.addNewPlanPackageParentCallback}
                securityScopes={this.props.securityScopes}
                totalPaid={BillingService.calculateTotalPaid(this.state.invoices)}
            />
        );
    }

    private addNewPlanPackageParentCallback(callback: any): void {
        this.callback = callback;
    }

    private renderDeleteUserButton(): JSX.Element {
        const activeFilespaces = this.props.domain?.filespaces.filter(filespace => !filespace.isDeleted);
        const hasActiveFilespaces = !!activeFilespaces && activeFilespaces.length > 0;
        if (
            (
                this.props.securityScopes.includes(SecurityScope.Master) ||
                this.props.securityScopes.includes(SecurityScope.AdminRemoveAccount)
            ) &&
            (
                !hasActiveFilespaces
            )
        ) {
            return (
                <button
                    className="btn warning"
                    onClick={this.showConfirmDeleteModal}
                >
                    Delete
                </button>
            );
        }
        return <></>;
    }

    private getNumberOfFilespaces(): number {

        if (!this.props.domain) {
            return 0;
        }

        return this.props.domain.filespaces.length;
    }

    private showConfirmDeleteModal(): void {
        this.setState({ isDeleteUserModalShown: true });
    }

    private hideConfirmDeleteModal(): void {
        this.setState({ isDeleteUserModalShown: false });
        this.props.onSuccess();
    }

    private async fetchInvoices(): Promise<void> {
        try {
            const invoices = await BillingService.fetchInvoices(this.props.user.id);
            this.setState({ invoices: invoices });
        } catch (e) {
        }
    }

    private renderConfirmDeleteModal(): JSX.Element {
        return (
            <UserDeleteConfirmModal
                userEmail={this.props.user?.email}
                userId={this.props.user?.id}
                isModalShown={this.state.isDeleteUserModalShown}
                closeModal={this.hideConfirmDeleteModal}
                activeFilespaces={this.getNumberOfActiveFilespaces()}
                allFilespaces={this.getNumberOfFilespaces()}
                totalPaid={BillingService.calculateTotalPaid(this.state.invoices)}
                invoiceAboveZero={BillingService.getInvoicesAboveZeroAmount(this.state.invoices)}
            />
        );
    }

}

export { User } ;



// WEBPACK FOOTER //
// ./src/backoffice/components/common/User.tsx